import { NewBookingButton } from '@components/Common/NewBookingButton';
import { UserGroupDropdown } from '@components/Layouts/UserGroupDropdown';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useIsMobile } from 'noddi-ui-web';
import { DepartmentOrganizationName, type UserSessionData } from 'noddi-web';
import { useCustomerDepartmentSelection } from '../../../hooks/useCustomerDepartmentSelection';
import { useUserGroupSelection } from '../../../hooks/useUserGroupSelection';
import { isUserGroupOrganizationContext } from '../../../utils/userGroupContext';

type HomePageHeaderProps = {
  userData: UserSessionData;
  isFirstTimeCustomer: boolean;
};

// The header nav carries the Book CTA on desktop, so this header only supplies the mobile
// one — never both, and never neither.
export const HomePageHeader = ({ userData, isFirstTimeCustomer }: HomePageHeaderProps) => {
  const isMobile = useIsMobile();
  const { hasMultipleSelectableUserGroups, selectedUserGroup } = useUserGroupSelection();
  const { selectedDepartment } = useCustomerDepartmentSelection();

  const title = `${t`Hi`} ${userData.user.firstName}!`;
  const isOrgContext = isUserGroupOrganizationContext(selectedUserGroup);

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex items-center justify-between gap-5'>
        <div className='flex flex-col gap-2'>
          <h1 className='text-4xl font-bold text-primary md:text-5xl'>{title}</h1>
          {isOrgContext && selectedUserGroup && (
            // muted-foreground is far too light on the brand page gradient; a translucent
            // primary stays readable and picks up whatever primary the brand defines.
            <div className='flex flex-col gap-1 text-primary'>
              <span className='text-xs font-medium text-primary/75'>
                <Trans>On behalf of</Trans>
              </span>
              {selectedDepartment ? (
                <DepartmentOrganizationName
                  departmentName={selectedDepartment.name}
                  organizationName={selectedDepartment.organizationName}
                  tone='brand'
                />
              ) : (
                <span className='font-semibold'>{selectedUserGroup.name}</span>
              )}
            </div>
          )}
          {!isOrgContext && !isFirstTimeCustomer && !hasMultipleSelectableUserGroups && (
            <p className='font-semibold text-secondary md:text-lg'>
              <Trans>Welcome back!</Trans>
            </p>
          )}
          {hasMultipleSelectableUserGroups && (
            <div className='w-fit max-w-72 min-w-52'>
              <UserGroupDropdown />
            </div>
          )}
        </div>
      </div>
      {isMobile && <NewBookingButton />}
    </div>
  );
};
